// The pause menu's chrome — structure only. What each screen *says* is U3's;
// this is the plate they all sit in (screen-mock `#pausePanel`, `#pauseNav`,
// `#pauseBody`, `.setrow`).
//
// Two columns because the pause menu is a small site rather than a dialog:
// Settings, Rules, Costs, Powers, Feedback, Restart, Main menu, and a
// diagnostics line at the foot (PLAN-UI §28.3). The nav is a fixed column so
// switching screens never moves the thing you are aiming at; the body scrolls
// on its own so the plate never grows past the viewport.

import { styled } from "@mui/material/styles";
import type { ComponentProps, ReactNode } from "react";

import { Panel } from "./Panel";
import { motion } from "./motion";

const Plate = styled(Panel)({
  // Most of the screen, not a dialog's worth: the rules and the costs tables
  // are read in here, and the designer's ruling on the first cut was that a
  // 760px plate left the game screen's worth of room around it unused.
  width: "min(1120px, 94vw)",
  height: "min(760px, 90vh)",
  display: "flex",
  overflow: "hidden"
});

const Nav = styled("div")(({ theme }) => ({
  width: 232,
  flex: "none",
  borderRight: `2px solid ${theme.kit.color.line}`,
  padding: `${theme.kit.space.lg}px 0`,
  display: "flex",
  flexDirection: "column"
}));

const Body = styled("div")({
  flex: 1,
  padding: "22px 26px",
  overflow: "auto",
  display: "flex",
  flexDirection: "column"
});

export interface PauseShellProps extends ComponentProps<"div"> {
  /** The nav column's contents — a title and a stack of `PauseNavButton`s. */
  nav?: ReactNode;
}

export function PauseShell({ nav, children, ...rest }: PauseShellProps) {
  return (
    <Plate {...rest}>
      <Nav>{nav}</Nav>
      <Body>{children}</Body>
    </Plate>
  );
}

/**
 * One row of the nav. `on` is the screen being shown; `exit` is the pair at
 * the foot that leave the game.
 */
export const PauseNavButton = styled("button", {
  shouldForwardProp: (prop: string) => prop !== "on" && prop !== "exit"
})<{ on?: boolean; exit?: boolean }>(({ theme, on, exit }) => ({
  textAlign: "left",
  background: on
    ? `color-mix(in srgb, ${theme.kit.color.brand} 6%, transparent)`
    : "none",
  border: 0,
  // The lit rail is a border rather than a background bar, so the row's text
  // sits in the same place whether it is on or off.
  borderLeft: `3px solid ${on ? theme.kit.color.brand : "transparent"}`,
  color: on ? theme.kit.color.brand : exit ? theme.kit.color.rust : theme.kit.color.inkDim,
  fontFamily: theme.kit.font.body,
  fontWeight: 700,
  fontSize: 14,
  padding: "9px 20px",
  cursor: "pointer",
  ...motion([], ["color", "background", "border-color"]),

  "&:hover": { color: on ? theme.kit.color.brand : theme.kit.color.ink },
  "&:focus-visible": {
    outline: `2px solid ${theme.kit.color.brand}`,
    outlineOffset: -2
  },
  "&:disabled": { opacity: 0.38, pointerEvents: "none" },

  "@media (pointer: coarse)": { minHeight: theme.kit.touchTarget }
}));

/** A labelled setting and its control. Ruled between, never above the first. */
export const SettingRow = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: theme.kit.space.md,
  padding: "8px 0",
  borderTop: `1px solid ${theme.kit.color.line}`,
  fontSize: 13.5,
  "&:first-of-type": { borderTop: 0 }
}));
